/**
 * Раздел «Медиа» §24.
 *
 * Повторное использование готового материала не требует повторной генерации —
 * поэтому всё, что система когда-либо создала, попадает сюда и остаётся доступным.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { AssetKind } from '@alterium/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { MediaCard } from '../components/media/MediaCard';
import { assetsApi } from '../api/assets';
import { uploadFile } from '../features/upload/uploadFile';
import { useTranslation } from 'react-i18next';

/** Фильтры — данные: держим ключ перевода, а не готовый текст. */
const filters: Array<{ value: AssetKind | 'all'; labelKey: string }> = [
  { value: 'all', labelKey: 'media.filter.all' },
  { value: AssetKind.VIDEO, labelKey: 'enum.assetKind.video' },
  { value: AssetKind.IMAGE, labelKey: 'enum.assetKind.image' },
  { value: AssetKind.AUDIO, labelKey: 'enum.assetKind.audio' },
  { value: AssetKind.DOCUMENT, labelKey: 'enum.assetKind.document' },
];

export function MediaPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<AssetKind | 'all'>('all');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const media = useQuery({
    queryKey: ['media', kind],
    queryFn: () => assetsApi.media(kind === 'all' ? {} : { kind }),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['media'] });

  const upload = useMutation({
    mutationFn: async (files: FileList) => {
      for (const file of Array.from(files)) {
        await uploadFile(file, {
          onProgress: ({ loaded, total }) => setProgress(Math.round((loaded / total) * 100)),
        });
      }
    },
    onMutate: () => {
      setError(null);
      setProgress(0);
    },
    onError: (cause: Error) => setError(cause.message),
    onSettled: async () => {
      setProgress(null);
      if (fileInput.current) fileInput.current.value = '';
      // Метаданные и превью появляются джобой чуть позже загрузки.
      await refresh();
      setTimeout(() => void refresh(), 2500);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => assetsApi.remove(id),
    onSuccess: refresh,
  });

  const assets = media.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title={t('nav.media')}
        description={t('media.subtitle')}
        action={
          <Button variant="primary" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
            <Upload className="h-4 w-4" strokeWidth={2} />
            {upload.isPending ? t('media.uploading') : t('media.upload')}
          </Button>
        }
      />

      <input
        ref={fileInput}
        type="file"
        multiple
        accept="video/*,image/*,audio/*,application/pdf"
        className="hidden"
        onChange={(event) => {
          const files = event.target.files;
          if (files && files.length > 0) upload.mutate(files);
        }}
      />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        {filters.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setKind(filter.value)}
            className={
              kind === filter.value
                ? 'rounded-control border border-line-strong bg-surface-hover px-3 py-1.5 text-meta text-content'
                : 'rounded-control border border-line px-3 py-1.5 text-meta text-content-secondary transition-colors duration-fast hover:border-line-strong hover:text-content'
            }
          >
            {t(filter.labelKey)}
          </button>
        ))}
      </div>

      {progress !== null ? (
        <div className="mb-6">
          <div className="progress-track">
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="mb-6">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {assets.length === 0 && !media.isLoading ? (
        <EmptyState
          title={t('media.empty.title')}
          description={t('media.empty.description')}
          action={
            <Button variant="primary" onClick={() => fileInput.current?.click()}>{t('media.upload')}</Button>
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {assets.map((asset) => (
            <MediaCard key={asset.id} asset={asset} onDelete={(id: string) => remove.mutate(id)} />
          ))}
        </div>
      )}
    </div>
  );
}
